import { useCallback, useEffect, useState } from 'react';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { formatDateTime } from '../../../utils/format';
import { InviteDelivery } from './InviteDelivery';

/**
 * Pending invitations across the given organizations, with Resend (mints a
 * new one-time link and re-emails it) and Revoke.
 */
export function PendingInvitationsList({ organizations, refreshToken = 0, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [issued, setIssued] = useState(null);

  const orgs = Array.isArray(organizations) ? organizations.filter((org) => org?.id) : [];
  const orgKey = orgs.map((org) => org.id).join(',');

  const load = useCallback(async () => {
    if (orgs.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(
        orgs.map(async (org) => {
          const list = await providerAdminApi.listInvitations(org.id);
          return (Array.isArray(list) ? list : [])
            .filter((row) => row.status === 'PENDING')
            .map((row) => ({ ...row, organization_name: org.name, org_type: org.org_type }));
        })
      );
      setRows(results.flat().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
    } catch (err) {
      setError(err.message || 'Failed to load invitations');
      setRows([]);
    } finally {
      setLoading(false);
    }
    // orgKey captures the org ids; orgs itself is a fresh array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgKey]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  async function resend(row) {
    setBusyId(row.id);
    setError(null);
    setIssued(null);
    try {
      const result = await providerAdminApi.resendInvitation(row.organization_id, row.id);
      setIssued(result);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message || 'Resend failed');
    } finally {
      setBusyId('');
    }
  }

  async function revoke(row) {
    setBusyId(row.id);
    setError(null);
    setIssued(null);
    try {
      await providerAdminApi.revokeInvitation(row.organization_id, row.id);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message || 'Revoke failed');
    } finally {
      setBusyId('');
    }
  }

  return (
    <div className="space-y-3">
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      <InviteDelivery result={issued} />
      {loading ? (
        <p className="text-sm text-eorig-muted">Loading invitations…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-eorig-muted">No pending invitations.</p>
      ) : (
        <ul className="divide-y divide-eorig-border rounded-2xl border border-eorig-border bg-white text-sm">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-eorig-ink">{row.email}</p>
                <p className="text-xs text-eorig-muted">
                  {row.organization_name} · {row.org_type} · {row.role} · expires{' '}
                  {formatDateTime(row.expires_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => resend(row)}
                disabled={busyId === row.id}
                className="rounded-xl bg-[#003A70] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                {busyId === row.id ? 'Working…' : 'Resend'}
              </button>
              <button
                type="button"
                onClick={() => revoke(row)}
                disabled={busyId === row.id}
                className="rounded-xl border border-eorig-border bg-white px-3 py-1.5 text-xs font-semibold text-eorig-ink disabled:opacity-50"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
